/**
 * Transaction process graph for product orders:
 *   - default-purchase
 *
 * This process mirrors the offer/bid flow from default-negotiation and
 * keeps the post-payment purchase flow (purchased/delivered/received).
 */

import { getBidsTransitions, getOfferTransitions } from './transactionHelpers';
import {
  getTransitionsWithMatchingOffers,
  isNegotiationState,
  isValidNegotiationOffersArray,
} from './transactionProcessNegotiation';

export { getTransitionsWithMatchingOffers, isNegotiationState, isValidNegotiationOffersArray };

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // Inquire is used before making an offer or requesting payment.
  INQUIRE: 'transition/inquire',

  // Make offer is available for the provider (reverse negotiation flow).
  MAKE_OFFER: 'transition/make-offer',
  MAKE_OFFER_AFTER_INQUIRY: 'transition/make-offer-after-inquiry',

  // Request quote is available for the customer (forward negotiation flow).
  REQUEST_QUOTE: 'transition/request-quote',
  MAKE_OFFER_FROM_REQUEST: 'transition/make-offer-from-request',
  REJECT_REQUEST: 'transition/reject-request',
  WITHDRAW_REQUEST: 'transition/withdraw-request',
  OPERATOR_REJECT_REQUEST: 'transition/operator-reject-request',

  // Reject offer is available for both parties and the marketplace operator.
  OPERATOR_REJECT_OFFER: 'transition/operator-reject-offer',
  CUSTOMER_REJECT_OFFER: 'transition/customer-reject-offer',
  PROVIDER_WITHDRAW_OFFER: 'transition/provider-withdraw-offer',

  // Transitions for provider-driven negotiation loop
  // TODO: this loop is not yet in use
  UPDATE_OFFER: 'transition/update-offer',
  ACCEPT_OFFER: 'transition/accept-offer',
  OPERATOR_ACCEPT_OFFER: 'transition/operator-accept-offer',
  UPDATE_FROM_UPDATE_PENDING: 'transition/update-from-update-pending',
  CUSTOMER_REJECT_FROM_UPDATE_PENDING: 'transition/customer-reject-from-update-pending',
  PROVIDER_WITHDRAW_FROM_UPDATE_PENDING: 'transition/provider-withdraw-from-update-pending',

  // Transitions for customer-driven price negotiation loop: customer makes a counter offer.
  CUSTOMER_MAKE_COUNTER_OFFER: 'transition/customer-make-counter-offer',
  PROVIDER_MAKE_COUNTER_OFFER: 'transition/provider-make-counter-offer',
  PROVIDER_ACCEPT_COUNTER_OFFER: 'transition/provider-accept-counter-offer',

  CUSTOMER_WITHDRAW_COUNTER_OFFER: 'transition/customer-withdraw-counter-offer',
  OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER:
    'transition/operator-reject-from-customer-counter-offer',
  PROVIDER_REJECT_COUNTER_OFFER: 'transition/provider-reject-counter-offer',

  // When the customer is satisfied with the offer,
  // they can start the payment process.
  REQUEST_PAYMENT_TO_ACCEPT_OFFER: 'transition/request-payment-to-accept-offer',
  REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED: 'transition/request-payment-after-offer-accepted',
  COLLECT_DOWN_PAYMENT_ACH_TO_ACCEPT_OFFER: 'transition/collect-down-payment-ach-to-accept-offer',
  COLLECT_DOWN_PAYMENT_ACH_AFTER_OFFER_ACCEPTED:
    'transition/collect-down-payment-ach-after-offer-accepted',

  // Purchase / buy-now transitions
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  COLLECT_DOWN_PAYMENT_ACH: 'transition/collect-down-payment-ach',
  COLLECT_DOWN_PAYMENT_ACH_AFTER_INQUIRY: 'transition/collect-down-payment-ach-after-inquiry',
  COLLECT_DOWN_PAYMENT_ACH_SUCCESS: 'transition/collect-down-payment-ach-success',
  CONFIRM_PAYMENT_WITH_ACH: 'transition/confirm-payment-with-ach',

  // Auction/bid transitions
  ...getBidsTransitions(),
  COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_PLACED: 'transition/collect-down-payment-ach-after-bid-placed',
  COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_TRANSFER:
    'transition/collect-down-payment-ach-after-bid-transfer',

  // Offer flow transitions (auction-style offers)
  ...getOfferTransitions(),

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Post-deposit workflow transitions
  CREATE_DUAL_BSA: 'transition/create-dual-bsa',
  GENERATE_BSAS: 'transition/generate-bsas',
  SEND_BSAS_VIA_DOCUSIGN: 'transition/send-bsas-via-docusign',
  MARK_BSAS_EXECUTED: 'transition/mark-bsas-executed',
  BSA_FULLY_EXECUTED: 'transition/bsa-fully-executed',
  BSA_FAILED: 'transition/bsa-failed',
  BYPASS_BSAS: 'transition/bypass-bsas',
  REQUEST_FINAL_PAYMENT: 'transition/request-final-payment',
  REQUEST_FINAL_PAYMENT_AFTER_BSA_BYPASS: 'transition/request-final-payment-after-bsa-bypass',
  SKIP_FINAL_PAYMENT: 'transition/skip-final-payment',
  SKIP_FINAL_PAYMENT_AFTER_BSA_BYPASS: 'transition/skip-final-payment-after-bsa-bypass',
  COLLECT_FINAL_PAYMENT: 'transition/collect-final-payment',
  FINAL_PAYMENT_RECEIVED: 'transition/final-payment-received',
  SETTLE_AND_RELEASE: 'transition/settle-and-release',
  AUTO_MARK_PURCHASED_AFTER_SETTLEMENT: 'transition/auto-mark-purchased-after-settlement',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This is the final successful transition in the purchase process.
  AUTO_COMPLETE: 'transition/auto-complete',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  QUOTE_REQUESTED: 'quote-requested',
  REQUEST_REJECTED: 'request-rejected',
  OFFER_PENDING: 'offer-pending',
  UPDATE_PENDING: 'update-pending',
  CUSTOMER_OFFER_PENDING: 'customer-offer-pending',
  OFFER_REJECTED: 'offer-rejected',
  OFFER_SENT: 'offer-sent',
  COUNTER_1: 'counter-1',
  COUNTER_2: 'counter-2',
  COUNTER_3: 'counter-3',
  OFFER_DECLINED: 'offer-declined',
  BID_SUBMITTED: 'bid-submitted',
  PAYMENT_IN_PROGRESS: 'payment-in-progress',
  BID_PAYMENT_EXPIRED: 'bid-payment-expire',
  BID_TRANSFER: 'bid-transfer',
  OFFER_ACCEPTED_LOCKED: 'offer-accepted-locked',
  DOWN_PAYMENT_PENDING: 'down-payment-pending',
  PENDING_PAYMENT: 'down-payment-pending',
  DOWN_PAYMENT_COLLECTED: 'down-payment-collected',
  BSA_PENDING: 'bsa-pending',
  BSA_SENT: 'bsa-sent',
  BSA_SIGNED: 'bsa-signed',
  BSA_FAILED: 'bsa-failed',
  BSA_BYPASSED: 'bsa-bypassed',
  // Legacy aliases retained for backward compatibility in existing components.
  BSAS_GENERATED: 'bsa-pending',
  DOCUSIGN_SENT: 'bsa-sent',
  BSAS_EXECUTED: 'bsa-signed',
  FINAL_PAYMENT_PENDING: 'final-payment-pending',
  FINAL_PAYMENT_COLLECTED: 'final-payment-collected',
  SETTLED_RELEASED: 'settled-released',
  PAYMENT_EXPIRED: 'payment-expired',
  OFFER_ACCEPTED: 'offer-accepted',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.MAKE_OFFER]: states.OFFER_PENDING,
        [transitions.REQUEST_QUOTE]: states.QUOTE_REQUESTED,
        [transitions.TRANSITION_SEND_OFFER]: states.OFFER_SENT,
        [transitions.PLACE_BID]: states.BID_SUBMITTED,
        [transitions.REQUEST_PAYMENT]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH]: states.DOWN_PAYMENT_PENDING,
      },
    },
    [states.QUOTE_REQUESTED]: {
      on: {
        [transitions.MAKE_OFFER_FROM_REQUEST]: states.OFFER_PENDING,
        [transitions.REJECT_REQUEST]: states.REQUEST_REJECTED,
        [transitions.WITHDRAW_REQUEST]: states.REQUEST_REJECTED,
        [transitions.OPERATOR_REJECT_REQUEST]: states.REQUEST_REJECTED,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.MAKE_OFFER_AFTER_INQUIRY]: states.OFFER_PENDING,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_INQUIRY]: states.DOWN_PAYMENT_PENDING,
      },
    },
    [states.OFFER_PENDING]: {
      on: {
        [transitions.CUSTOMER_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.PROVIDER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_MAKE_COUNTER_OFFER]: states.CUSTOMER_OFFER_PENDING,
        [transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_TO_ACCEPT_OFFER]: states.DOWN_PAYMENT_PENDING,
        [transitions.UPDATE_OFFER]: states.UPDATE_PENDING,
      },
    },
    [states.OFFER_SENT]: {
      on: {
        [transitions.TRANSITION_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.TRANSITION_COUNTER_OFFER]: states.COUNTER_1,
      },
    },
    [states.COUNTER_1]: {
      on: {
        [transitions.TRANSITION_ACCEPT_AFTER_COUNTER_1]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_AFTER_COUNTER_1]: states.OFFER_DECLINED,
        [transitions.TRANSITION_COUNTER_AFTER_COUNTER_1]: states.COUNTER_2,
      },
    },
    [states.COUNTER_2]: {
      on: {
        [transitions.TRANSITION_ACCEPT_AFTER_COUNTER_2]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_AFTER_COUNTER_2]: states.OFFER_DECLINED,
        [transitions.TRANSITION_COUNTER_AFTER_COUNTER_2]: states.COUNTER_3,
      },
    },
    [states.COUNTER_3]: {
      on: {
        [transitions.TRANSITION_ACCEPT_AFTER_COUNTER_3]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_AFTER_COUNTER_3]: states.OFFER_DECLINED,
      },
    },
    // Provider-driven negotiation loop
    // TODO: this is not yet in use
    [states.UPDATE_PENDING]: {
      on: {
        [transitions.ACCEPT_OFFER]: states.OFFER_PENDING,
        [transitions.OPERATOR_ACCEPT_OFFER]: states.OFFER_PENDING,
        [transitions.UPDATE_FROM_UPDATE_PENDING]: states.UPDATE_PENDING,
        [transitions.CUSTOMER_REJECT_FROM_UPDATE_PENDING]: states.OFFER_REJECTED,
        [transitions.PROVIDER_WITHDRAW_FROM_UPDATE_PENDING]: states.OFFER_REJECTED,
      },
    },
    // Customer-driven negotiation loop
    [states.CUSTOMER_OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_REJECT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_ACCEPT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_MAKE_COUNTER_OFFER]: states.OFFER_PENDING,
      },
    },
    [states.BID_SUBMITTED]: {
      on: {
        [transitions.UPDATE_BID_INFO]: states.BID_SUBMITTED,
        [transitions.UPDATE_BID]: states.BID_SUBMITTED,
        [transitions.INITIATE_BID_PAYMENT]: states.PAYMENT_IN_PROGRESS,
        [transitions.INITIATE_BID_PAYMENT_OPERATOR]: states.PAYMENT_IN_PROGRESS,
      },
    },
    [states.PAYMENT_IN_PROGRESS]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_BID_PLACED]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_PLACED]: states.DOWN_PAYMENT_PENDING,
        [transitions.BID_PAYMENT_EXPIRE]: states.BID_PAYMENT_EXPIRED,
      },
    },
    [states.BID_PAYMENT_EXPIRED]: {
      on: {
        [transitions.TRANSFER_BID]: states.BID_TRANSFER,
      },
    },
    [states.BID_TRANSFER]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_BID_TRANSFER]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_TRANSFER]: states.DOWN_PAYMENT_PENDING,
      },
    },

    [states.DOWN_PAYMENT_PENDING]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.DOWN_PAYMENT_COLLECTED,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_SUCCESS]: states.DOWN_PAYMENT_COLLECTED,
        [transitions.CONFIRM_PAYMENT_WITH_ACH]: states.DOWN_PAYMENT_COLLECTED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED]: states.DOWN_PAYMENT_PENDING,
        [transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_OFFER_ACCEPTED]: states.DOWN_PAYMENT_PENDING,
      },
    },
    [states.DOWN_PAYMENT_COLLECTED]: {
      on: {
        [transitions.CREATE_DUAL_BSA]: states.BSA_PENDING,
        [transitions.GENERATE_BSAS]: states.BSA_PENDING,
        [transitions.BYPASS_BSAS]: states.BSA_BYPASSED,
      },
    },
    [states.BSA_PENDING]: {
      on: {
        [transitions.SEND_BSAS_VIA_DOCUSIGN]: states.BSA_SENT,
        [transitions.BSA_FAILED]: states.BSA_FAILED,
      },
    },
    [states.BSA_SENT]: {
      on: {
        [transitions.MARK_BSAS_EXECUTED]: states.BSA_SIGNED,
        [transitions.BSA_FULLY_EXECUTED]: states.BSA_SIGNED,
        [transitions.BSA_FAILED]: states.BSA_FAILED,
      },
    },
    [states.BSA_SIGNED]: {
      on: {
        [transitions.REQUEST_FINAL_PAYMENT]: states.FINAL_PAYMENT_PENDING,
        [transitions.SKIP_FINAL_PAYMENT]: states.FINAL_PAYMENT_COLLECTED,
      },
    },
    [states.BSA_BYPASSED]: {
      on: {
        [transitions.REQUEST_FINAL_PAYMENT_AFTER_BSA_BYPASS]: states.FINAL_PAYMENT_PENDING,
        [transitions.SKIP_FINAL_PAYMENT_AFTER_BSA_BYPASS]: states.FINAL_PAYMENT_COLLECTED,
      },
    },
    [states.BSA_FAILED]: {},
    [states.FINAL_PAYMENT_PENDING]: {
      on: {
        [transitions.COLLECT_FINAL_PAYMENT]: states.FINAL_PAYMENT_COLLECTED,
        [transitions.FINAL_PAYMENT_RECEIVED]: states.FINAL_PAYMENT_COLLECTED,
      },
    },
    [states.FINAL_PAYMENT_COLLECTED]: {
      on: {
        [transitions.SETTLE_AND_RELEASE]: states.SETTLED_RELEASED,
      },
    },
    [states.SETTLED_RELEASED]: {
      on: {
        [transitions.AUTO_MARK_PURCHASED_AFTER_SETTLEMENT]: states.PURCHASED,
      },
    },
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: { type: 'final' },
    [states.REQUEST_REJECTED]: { type: 'final' },
    [states.OFFER_REJECTED]: { type: 'final' },
    [states.OFFER_DECLINED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.PLACE_BID,
    transitions.UPDATE_BID,
    transitions.UPDATE_BID_INFO,
    transitions.INITIATE_BID_PAYMENT,
    transitions.INITIATE_BID_PAYMENT_OPERATOR,
    transitions.BID_PAYMENT_EXPIRE,
    transitions.TRANSFER_BID,
    transitions.REQUEST_PAYMENT_AFTER_BID_PLACED,
    transitions.REQUEST_PAYMENT_AFTER_BID_TRANSFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_PLACED,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_TRANSFER,
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.COLLECT_DOWN_PAYMENT_ACH,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH_TO_ACCEPT_OFFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_OFFER_ACCEPTED,
    transitions.COLLECT_DOWN_PAYMENT_ACH_SUCCESS,
    transitions.TRANSITION_SEND_OFFER,
    transitions.TRANSITION_ACCEPT_OFFER,
    transitions.TRANSITION_DECLINE_OFFER,
    transitions.TRANSITION_COUNTER_OFFER,
    transitions.TRANSITION_ACCEPT_AFTER_COUNTER_1,
    transitions.TRANSITION_DECLINE_AFTER_COUNTER_1,
    transitions.TRANSITION_COUNTER_AFTER_COUNTER_1,
    transitions.TRANSITION_ACCEPT_AFTER_COUNTER_2,
    transitions.TRANSITION_DECLINE_AFTER_COUNTER_2,
    transitions.TRANSITION_COUNTER_AFTER_COUNTER_2,
    transitions.TRANSITION_ACCEPT_AFTER_COUNTER_3,
    transitions.TRANSITION_DECLINE_AFTER_COUNTER_3,
    transitions.TRANSITION_REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED,
    transitions.MAKE_OFFER,
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.REQUEST_QUOTE,
    transitions.MAKE_OFFER_FROM_REQUEST,
    transitions.REJECT_REQUEST,
    transitions.WITHDRAW_REQUEST,
    transitions.OPERATOR_REJECT_REQUEST,
    transitions.OPERATOR_REJECT_OFFER,
    transitions.CUSTOMER_REJECT_OFFER,
    transitions.PROVIDER_WITHDRAW_OFFER,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_ACCEPT_COUNTER_OFFER,
    transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER,
    transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
    transitions.EXPIRE_PAYMENT,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_ACH,
    transitions.CREATE_DUAL_BSA,
    transitions.GENERATE_BSAS,
    transitions.SEND_BSAS_VIA_DOCUSIGN,
    transitions.MARK_BSAS_EXECUTED,
    transitions.BSA_FULLY_EXECUTED,
    transitions.BSA_FAILED,
    transitions.BYPASS_BSAS,
    transitions.REQUEST_FINAL_PAYMENT,
    transitions.REQUEST_FINAL_PAYMENT_AFTER_BSA_BYPASS,
    transitions.SKIP_FINAL_PAYMENT,
    transitions.SKIP_FINAL_PAYMENT_AFTER_BSA_BYPASS,
    transitions.COLLECT_FINAL_PAYMENT,
    transitions.FINAL_PAYMENT_RECEIVED,
    transitions.SETTLE_AND_RELEASE,
    transitions.AUTO_MARK_PURCHASED_AFTER_SETTLEMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.MARK_RECEIVED_FROM_PURCHASED,
    transitions.MARK_RECEIVED,
    transitions.AUTO_MARK_RECEIVED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ].includes(transition);
};
export const isCustomerReview = _transition => {
  return false;
};

export const isProviderReview = _transition => {
  return false;
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.PLACE_BID,
    transitions.UPDATE_BID,
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_AFTER_BID_PLACED,
    transitions.REQUEST_PAYMENT_AFTER_BID_TRANSFER,
    transitions.REQUEST_PAYMENT_AFTER_OFFER_ACCEPTED,
    transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_INQUIRY,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_PLACED,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_BID_TRANSFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH_AFTER_OFFER_ACCEPTED,
    transitions.COLLECT_DOWN_PAYMENT_ACH_TO_ACCEPT_OFFER,
    transitions.COLLECT_DOWN_PAYMENT_ACH_SUCCESS,
    transitions.CONFIRM_PAYMENT_WITH_ACH,
    transitions.CREATE_DUAL_BSA,
    transitions.GENERATE_BSAS,
    transitions.SEND_BSAS_VIA_DOCUSIGN,
    transitions.MARK_BSAS_EXECUTED,
    transitions.BSA_FULLY_EXECUTED,
    transitions.BSA_FAILED,
    transitions.BYPASS_BSAS,
    transitions.SKIP_FINAL_PAYMENT,
    transitions.SKIP_FINAL_PAYMENT_AFTER_BSA_BYPASS,
    transitions.COLLECT_FINAL_PAYMENT,
    transitions.FINAL_PAYMENT_RECEIVED,
    transitions.SETTLE_AND_RELEASE,
    transitions.MAKE_OFFER,
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.MAKE_OFFER_FROM_REQUEST,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
  ].includes(transition);
};

// Check when transaction is completed.
export const isCompleted = transition => {
  const txCompletedTransitions = [transitions.AUTO_COMPLETE];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.QUOTE_REQUESTED,
  states.CUSTOMER_OFFER_PENDING,
  states.OFFER_SENT,
  states.COUNTER_2,
  states.BSA_PENDING,
  states.BSA_SENT,
  states.FINAL_PAYMENT_PENDING,
  states.PURCHASED,
];

export const statesNeedingCustomerAttention = [
  states.OFFER_PENDING,
  states.COUNTER_1,
  states.COUNTER_3,
  states.BSA_SIGNED,
  states.BSA_BYPASSED,
  states.FINAL_PAYMENT_PENDING,
  states.DELIVERED,
];
